import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { isUserAuthenticated } from '../../utils/auth';

const FeaturedDoctors = () => {
    const navigate = useNavigate();
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(true);

    const demoDoctors = [
        {
            id: 101,
            name: "Sarah Johnson",
            specialization: "Cardiologist",
            rating: 4.9,
            reviews: 120,
            image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=300&q=80",
            available: true
        },
        {
            id: 102,
            name: "Mark Williams",
            specialization: "Neurologist",
            rating: 4.8,
            reviews: 98,
            image: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?auto=format&fit=crop&w=300&q=80",
            available: false
        },
        {
            id: 103,
            name: "Emily Chen",
            specialization: "Pediatrician",
            rating: 5.0,
            reviews: 156,
            image: "https://images.unsplash.com/photo-1594824476967-48c8b964273f?auto=format&fit=crop&w=300&q=80",
            available: true
        },
        {
            id: 104,
            name: "James Wilson",
            specialization: "Orthopedic",
            rating: 4.7,
            reviews: 84,
            image: "https://images.unsplash.com/photo-1622253692010-333f2da6031d?auto=format&fit=crop&w=300&q=80",
            available: true
        }
    ];

    useEffect(() => {
        const fetchDoctors = async () => {
            try {
                const res = await api.get('user/doctor_list/');
                if (res.data && Array.isArray(res.data) && res.data.length > 0) {
                    const sortedDoctors = res.data.sort((a, b) => Number(b.rating) - Number(a.rating));
                    setDoctors(sortedDoctors.slice(0, 4));
                } else {
                    setDoctors(demoDoctors);
                }
            } catch (err) {
                console.error("Failed to fetch doctors, using demo data", err);
                setDoctors(demoDoctors);
            } finally {
                setLoading(false);
            }
        };

        fetchDoctors();
    }, []);

    const getImageUrl = (image) => {
        if (!image || (typeof image === 'string' && image.trim() === '')) {
            return "https://via.placeholder.com/300x250?text=Doctor";
        }
        if (image.startsWith('http')) return image;

        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        if (image.startsWith('/media/')) return `${apiUrl}${image}`;
        return `${apiUrl}/media/${image}`;
    };

    const handleViewProfile = (doctorId) => {
        if (isUserAuthenticated()) {
            navigate(`/user/doctor-profile/${doctorId}`);
        } else {
            navigate('/user/login');
        }
    };

    const handleBrowseAll = () => {
        if (isUserAuthenticated()) {
            navigate('/user/doctor_list');
        } else {
            navigate('/user/login');
        }
    };

    return (
        <section id="specializations" className="py-4 bg-light-soft">
            <div className="container py-5">
                <div className="text-center mb-5">
                    <h2 className="fw-bold mb-3">Top Rated Specialists</h2>
                    <p className="text-secondary mx-auto" style={{ maxWidth: '600px' }}>
                        Book appointments with highly qualified doctors across various specializations.
                    </p>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <div className="row g-4">
                        {doctors.length > 0 ? (
                            doctors.map((doc) => (
                                <div key={doc.id} className="col-md-6 col-lg-3">
                                    <div className="card h-100 border-0 shadow-hover rounded-4 overflow-hidden bg-white transition-all">
                                        <div className="position-relative">
                                            <img
                                                src={getImageUrl(doc.image)}
                                                className="card-img-top object-fit-cover"
                                                alt={doc.name}
                                                style={{ height: '250px' }}
                                                onError={(e) => e.target.src = "https://via.placeholder.com/300x250?text=Doctor"}
                                            />
                                            {/* Assuming availability might be separate or part of doc object. 
                                                For list view, usually just basic info. 
                                            */}
                                        </div>
                                        <div className="card-body p-4 text-center">
                                            <h5 className="fw-bold mb-1"> {doc.name}</h5>
                                            <p className="text-primary small fw-semibold mb-2">{doc.specialization || 'Specialist'}</p>

                                            <div className="d-flex justify-content-center align-items-center mb-3 text-warning small">
                                                <i className="bi bi-star-fill me-1"></i>
                                                <span className="fw-bold text-dark me-1">{doc.rating || 'New'}</span>
                                                <span className="text-muted">({doc.reviews || '0'})</span>
                                            </div>

                                            <button
                                                className="btn btn-outline-primary w-100 rounded-pill btn-sm fw-semibold"
                                                onClick={() => handleViewProfile(doc.id)}
                                            >
                                                View Profile
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="col-12 text-center py-4">
                                <div className="p-4 bg-white rounded-4 shadow-sm d-inline-block">
                                    <p className="text-muted mb-3">Login to view all available specialists.</p>
                                    <button
                                        className="btn btn-primary rounded-pill px-4"
                                        onClick={() => navigate('/user/login')}
                                    >
                                        Login Now
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="text-center mt-5">
                    <button
                        className="btn btn-primary rounded-pill px-5 py-2 fw-semibold shadow-sm"
                        onClick={handleBrowseAll}
                    >
                        Browse All Doctors
                    </button>
                </div>
            </div>
        </section>
    );
};

export default FeaturedDoctors;
